import React from 'react';
import { Button } from '../common';

interface PracticeNoContentProps {
  onBack: () => void;
  selectedSubtopics?: number[];
  selectedCompanyTags?: number[];
}

export const PracticeNoContent: React.FC<PracticeNoContentProps> = ({
  onBack,
  selectedSubtopics,
  selectedCompanyTags
}) => {
  const hasFilters = (selectedSubtopics && selectedSubtopics.length > 0) ||
                    (selectedCompanyTags && selectedCompanyTags.length > 0);

  return (
    <div className="max-w-4xl mx-auto px-4 py-12">
      <div className="text-center">
        {/* Icon */}
        <div className="mb-8">
          <div className="w-24 h-24 mx-auto bg-gradient-to-br from-blue-100 to-indigo-100 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-full flex items-center justify-center">
            <svg className="w-12 h-12 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
          </div>
        </div>

        {/* Title */}
        <h1 className="text-3xl font-bold mb-4 bg-gradient-to-r from-blue-600 via-indigo-500 to-purple-600 bg-clip-text text-transparent">
          No Practice Content Available
        </h1>

        {/* Message */}
        <div className="max-w-2xl mx-auto">
          <p className="text-lg text-gray-600 dark:text-gray-400 mb-6">
            {hasFilters ? (
              <>
                We couldn't find any practice content matching your current selections.
                This might be because:
              </>
            ) : (
              "We couldn't find any practice content available at the moment."
            )}
          </p>

          {hasFilters && (
            <div className="bg-blue-50/50 dark:bg-blue-900/20 rounded-xl p-6 mb-8 text-left">
              <h3 className="font-semibold text-blue-900 dark:text-blue-300 mb-3">
                Possible reasons:
              </h3>
              <ul className="space-y-2 text-blue-800 dark:text-blue-200">
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  <span>Content for your selected topics or companies hasn't been added yet</span>
                </li>
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  <span>You've already practiced all available content in these categories</span>
                </li>
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  <span>Content might be temporarily unavailable</span>
                </li>
              </ul>
            </div>
          )}

          <div className="bg-gray-50/50 dark:bg-gray-800/50 rounded-xl p-6 mb-8">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-3">
              What you can do:
            </h3>
            <ul className="space-y-2 text-gray-700 dark:text-gray-300">
              <li className="flex items-start">
                <span className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                <span>Try selecting different topics or companies</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                <span>Practice without filters to access all available content</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                <span>Check back later for new content</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button
            onClick={onBack}
            variant="primary"
            className="px-8 py-3 text-lg font-medium rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Selection
          </Button>

          <Button
            onClick={() => window.location.href = '/dashboard'}
            variant="outline"
            className="px-8 py-3 text-lg font-medium rounded-xl border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
            Go to Dashboard
          </Button>
        </div>
      </div>
    </div>
  );
};